import type Excel from 'exceljs';

import { APP_NAME, SheetExportTypes } from '../xlsxExport.constants';

import metadataBuilder from './metadataBuilder';
import isExportFormatForNumber from '../utils/isExportFormatForNumber';
import gridStyleBuilder from './styleBuilders/gridStyleBuilder';
import { CellStyleConfig, ColumnDefinition } from '../types';

export const setValueType = (value: unknown, exportFormat?: string) =>
  isExportFormatForNumber(exportFormat) ? Number(value) : value;

export const formatTo2DArray = (data: Record<string, unknown>[], columnDefinitions: ColumnDefinition[]) =>
  data.map(row =>
    columnDefinitions.reduce((acc, columnDef) => {
      // Support both new customExport format and legacy direct properties
      const exportFormat = columnDef.customExport?.format || columnDef.exportFormat;
      const omitExportIfNull = columnDef.customExport?.omitIfNull ?? columnDef.omitExportIfNull;
      const exportMapValue = columnDef.customExport?.mapValue || columnDef.exportMapValue;

      const { field, valueGetter } = columnDef;
      const getValueFromRow = columnDef.customExport?.getValueFromRow;
      const value = (() => {
        if (getValueFromRow && typeof getValueFromRow === 'function') {
          const v = getValueFromRow(row);
          return exportMapValue ? exportMapValue(v) : v;
        }
        if (valueGetter && typeof valueGetter === 'function') {
          const getValue = (f: string) => row[f];
          const resolved = valueGetter({ data: row, getValue } as Parameters<typeof valueGetter>[0]);
          return exportMapValue ? exportMapValue(resolved) : resolved;
        }
        if (!field) {
          return null;
        }
        if (exportMapValue) {
          return exportMapValue(row[field]);
        }
        return row[field];
      })();

      if (value === null && omitExportIfNull) {
        return [...acc, null];
      }

      const typedValue = setValueType(value, exportFormat);

      return [...acc, typedValue];
    }, [] as unknown[])
  );

export const buildHeaderRow = (columnDefinitions: ColumnDefinition[]) =>
  columnDefinitions.map(columnDef => columnDef.headerName);


type RowNumber = number;

export type GridBuilderSheet<Data extends Record<string, unknown> = Record<string, unknown>> = {
    type: SheetExportTypes.GRID,
    name: string,
    data: Data[],
    columnDefinitions: ColumnDefinition[],
    additionalMetadata?: {
        value?: string | number | null,
    }[],
    descriptionColumnIndex?: number,
    options: {
        numberOfColumnsToStick: number,
        hideDecimals?: boolean,
        negativeMinus?: boolean,
        shouldDisplayZero?: boolean,
        addTimestamp?: boolean,
        headerRowHeight: number,
        rowStyles?: Record<RowNumber, Excel.Style>,
        cellStyles?: CellStyleConfig[],
        createAutoFilter?: boolean,
    }
}

type GridBuilderOptions = {
    description?: string,
    disableHeaderRendering?: boolean,
    periodName?: string,
}

const gridBuilder = (sheet: GridBuilderSheet, options: GridBuilderOptions = {}) => {
  const {
    data,
    columnDefinitions,
    additionalMetadata,
    descriptionColumnIndex,
    options: sheetOptions,
  } = sheet;

  const {
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    addTimestamp,
    headerRowHeight,
    rowStyles = {},
    cellStyles = [],
    createAutoFilter,
  } = sheetOptions;

  const { description, disableHeaderRendering, periodName } = options;

  const unpackedAdditionalMetadataRows = additionalMetadata
    ? additionalMetadata.map(row => row.value)
    : [];

  const additionalMetadataRowsWithEmptyRow = [
    ...unpackedAdditionalMetadataRows,
    '',
  ];

  const sheetMetadata = disableHeaderRendering
    ? []
    : metadataBuilder({
      appName: APP_NAME,
      sheetTitle: sheet.name,
      sheetDescription: description,
      periodName,
      addTimestamp,
      additionalMetadataRows: additionalMetadataRowsWithEmptyRow,
    });

  const headerRow = buildHeaderRow(columnDefinitions);
  const dataGrid = formatTo2DArray(data, columnDefinitions);

  const grid = [...sheetMetadata, headerRow, ...dataGrid];

  const HEADER_ROW = 1;

  const metadata = gridStyleBuilder({
    sheetName: sheet.name.slice(0, 31),
    numberOfRowsToStick: sheetMetadata.length + HEADER_ROW,
    numberOfColumnsToStick: sheet.options?.numberOfColumnsToStick,
    columnDefinitions,
    cellStyles,
    sheetMetadataLength: sheetMetadata.length,
    descriptionColumnIndex,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    headerRowHeight,
    rowStyles,
    rowsCount: dataGrid.length,
    createAutoFilter,
  });

  return {
    metadata,
    grid,
  };
};

export default gridBuilder;
